import React, { useEffect, useState } from 'react';
import Markdown from 'markdown-to-jsx';
import { Wrench } from 'lucide-react';
import { getMaintenanceHistory } from '../../../shared/api/publicApi';
import { parseServerTime } from '../../../shared/utils/format';
import '../../../styles/features/incidents/MaintenanceHistory.css';

const HISTORY_DAYS = 90;

function formatRange(start, end) {
  const s = new Date(start);
  const e = new Date(end);
  const day = { month: 'short', day: 'numeric', year: 'numeric' };
  const time = { hour: '2-digit', minute: '2-digit' };
  const sameDay = s.toDateString() === e.toDateString();
  return sameDay
    ? `${s.toLocaleDateString(undefined, day)}, ${s.toLocaleTimeString(undefined, time)} – ${e.toLocaleTimeString(undefined, time)}`
    : `${s.toLocaleString(undefined, { ...day, ...time })} – ${e.toLocaleString(undefined, { ...day, ...time })}`;
}

function formatDuration(start, end) {
  const minutes = Math.max(0, Math.round((new Date(end) - new Date(start)) / 60000));
  if (minutes < 60) return `${minutes}m`;
  if (minutes < 1440) return `${Math.round(minutes / 60)}h`;
  return `${Math.round(minutes / 1440)}d`;
}

function MaintenanceHistory() {
  const [history, setHistory] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getMaintenanceHistory(HISTORY_DAYS)
      .then((res) => { if (!cancelled) setHistory(res.data?.history || []); })
      .catch(() => { if (!cancelled) setHistory([]); });
    return () => { cancelled = true; };
  }, []);

  if (!history) return null;

  return (
    <div className="incident-history maintenance-history">
      <h3 className="incident-history__title">Maintenance History</h3>

      {history.length === 0 ? (
        <div className="no-incidents">
          <p>No maintenance in the past {HISTORY_DAYS} days.</p>
        </div>
      ) : (
        <ul className="maint-history_list">
          {history.map((item) => {
            const expanded = expandedId === item.id;
            const hasDetail = Boolean(item.description) || item.updates.length > 0;
            const scope = item.endpoints.map((e) => e.name).join(', ');
            return (
              <li key={item.id} className={`maint-history_item ${expanded ? 'is-expanded' : ''}`}>
                <button
                  type="button"
                  className="maint-history_header"
                  onClick={() => hasDetail && setExpandedId(expanded ? null : item.id)}
                  aria-expanded={hasDetail ? expanded : undefined}
                  disabled={!hasDetail}
                >
                  <Wrench size={14} className="maint-history_icon" />
                  <div className="maint-history_main">
                    <span className="maint-history_title">{item.title}</span>
                    <span className="maint-history_meta">
                      {formatRange(item.start_time, item.end_time)}
                      {' · '}
                      {scope || 'All services'}
                    </span>
                  </div>
                  <span className="maint-history_duration">{formatDuration(item.start_time, item.end_time)}</span>
                  {hasDetail && <span className="maint-history_toggle" aria-hidden="true">{expanded ? '▲' : '▼'}</span>}
                </button>

                {expanded && (
                  <div className="maint-history_body">
                    {item.description && (
                      <div className="maint-history_desc">
                        <Markdown options={{ disableParsingRawHTML: true }}>{item.description}</Markdown>
                      </div>
                    )}
                    {item.updates.map((u) => (
                      <div key={u.id} className="maint-history_update">
                        <span className="maint-history_update-time">
                          {parseServerTime(u.created_at)?.toLocaleString(undefined, {
                            month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                          })}
                        </span>
                        <div className="maint-history_update-message">
                          <Markdown options={{ disableParsingRawHTML: true }}>{u.message}</Markdown>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default MaintenanceHistory;
